/* eslint formatjs/no-literal-string-in-jsx: "error" */
import {
  ArrowLink,
  Button,
  Container,
  FlexContainer,
  H2,
  H4,
  Icon,
  Menu,
  Modal,
  Stack,
  Text,
  TextLink,
  useBreakpointValue
} from "@carrotfertility/carotene"
import React from "react"
import { Guide, Section } from "../utils/utils"
import styled from "styled-components"
import { FormattedMessage, useIntl } from "react-intl"

const LeaveGuide = ({ onClick }: { onClick: () => void }): JSX.Element => {
  const intl = useIntl()
  return (
    <Container>
      <TextLink onClick={onClick} size="medium">
        <Text weight="bold">
          <Stack direction="row" spacing="small" id="leave-guide-link">
            <Icon
              name="upArrow"
              color="carrotOrange"
              title={intl.formatMessage({ defaultMessage: "leave", description: "leave the guide" })}
            />
            <FormattedMessage defaultMessage="Leave guide" />
          </Stack>
        </Text>
      </TextLink>
    </Container>
  )
}

const SectionsList = ({
  sections,
  onSectionSelect
}: {
  sections: Section[]
  onSectionSelect: (pageIndex: number) => void
}): JSX.Element => {
  return (
    <Container id="sections-list">
      <H4 sx={{ color: "black60" }}>
        <FormattedMessage defaultMessage="Jump to section" />
      </H4>
      <Stack padding="16px 0 0 12px" spacing="small">
        {sections.map(({ title }, i) => (
          <TextLink
            className="section-link"
            variant="no-underline"
            key={title}
            onClick={() => onSectionSelect(i)}
            size="medium"
          >
            {title}
          </TextLink>
        ))}
      </Stack>
    </Container>
  )
}
const DownloadLink = ({ link }: { link: string }): JSX.Element => {
  return (
    <Container>
      <H4 sx={{ color: "black60" }}>
        <FormattedMessage defaultMessage="Resource" />
      </H4>
      <FlexContainer padding="16px 0 0 12px">
        <ArrowLink href={link} target={link} className="download-worksheet-link" size="medium" color="black80">
          <FormattedMessage
            defaultMessage="Download worksheet"
            description="Link to download a pdf worksheet that accompanies an educational guide. The pdf is a resource for the user to use while going through the guide. It's intended to be used as a tool for weighing pros and cons and recording their preferences."
          />
        </ArrowLink>
      </FlexContainer>
    </Container>
  )
}

const ExitModal = ({
  open,
  setOpen,
  onExit
}: {
  open: boolean
  setOpen: (open) => void
  onExit: () => void
}): JSX.Element => {
  const intl = useIntl()
  const formattedContinue = intl.formatMessage({ defaultMessage: "Continue" })
  const formattedContinueGuide = intl.formatMessage({ defaultMessage: "Continue guide" })
  const continueButtonText = useBreakpointValue<string>([
    formattedContinue,
    formattedContinue,
    formattedContinueGuide,
    formattedContinueGuide
  ])
  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Container padding="large">
          <Stack spacing="giant">
            <Stack spacing="tiny">
              <H2>
                <FormattedMessage defaultMessage="Are you sure you want to leave?" />
              </H2>
              <Text>
                <FormattedMessage defaultMessage="If you leave, you'll lose your progress and won't be able to pick up where you left off." />
              </Text>
            </Stack>
            <FlexContainer
              direction="row"
              justifyContent={["flex-start", "flex-start", "flex-end", "flex-end"]}
              alignItems={["start", "start", "end", "end"]}
              gap="small"
            >
              <Button variant="secondary" onClick={() => setOpen(false)} id="continue-guide-modal-button">
                {continueButtonText}
              </Button>
              <Button variant="primary" onClick={onExit} id="leave-guide-modal-button">
                <FormattedMessage defaultMessage="Leave guide" />
              </Button>
            </FlexContainer>
          </Stack>
        </Container>
      </Modal>
    </>
  )
}

const MenuLink = styled.a`
  :hover {
    cursor: pointer;
  }
`

export const PageNavigation = ({
  onExit,
  guide,
  onPageChange
}: {
  onExit: () => void
  guide: Guide
  onPageChange: (sectionIndex: number) => void
}): JSX.Element => {
  const [open, setOpen] = React.useState(false)
  const [exitOpen, setExitOpen] = React.useState(false)
  const intl = useIntl()

  return (
    <>
      <Menu
        isDismissable
        open={open}
        trigger="custom"
        onClose={() => setOpen(false)}
        anchor={
          <MenuLink onClick={() => setOpen(!open)}>
            <FlexContainer
              width="28px"
              height="28px"
              borderRadius="14px"
              background={open || exitOpen ? "veryLightCarrotOrange" : "none"}
              sx={{ "&:hover": { backgroundColor: "veryLightCarrotOrange" } }}
              alignItems="center"
              justifyContent="center"
              padding={"0 0 0 3px"}
            >
              <Icon
                name="meatballs"
                color="carrotOrange"
                size="medium"
                title={intl.formatMessage({ defaultMessage: "menu", description: "navigation menu for the guide" })}
              />
            </FlexContainer>
          </MenuLink>
        }
      >
        <Stack width="300px" paddingLeftRight="medium" paddingTopBottom="medium">
          <SectionsList
            sections={guide.sections}
            onSectionSelect={(sectionIndex) => {
              setOpen(false)
              onPageChange(sectionIndex)
            }}
          />
          {guide.companionDocument && <DownloadLink link={guide.companionDocument.file.url} />}
          <Container sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.1)" }} />
          <LeaveGuide
            onClick={() => {
              setOpen(false)
              setExitOpen(true)
            }}
          />
        </Stack>
      </Menu>
      <ExitModal open={exitOpen} setOpen={setExitOpen} onExit={onExit} />
    </>
  )
}
